import React from 'react';
import {
  Paper, Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, MenuItem, IconButton,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Addon, AddonOption, AddonOptionPricingRow } from '../../types';
import {
  OtherLineItem, SelectOption, NormalizedDefaultAddon,
  HEADER_CELL_SX, TABLE_CELL_SX, SPACER_CELL_SX,
  COL, CELL_W, NO_SPIN_SX, rowErrorSX,
} from './types';
import { formatCurrency } from '../../utils/formatCurrency';

interface OtherItemsTableProps {
  otherLineItems: OtherLineItem[];
  addons: Addon[];
  filteredAddons: Addon[];
  addonOptions: Record<number, AddonOption[]>;
  addonOptionPricing: Record<string, AddonOptionPricingRow>;
  defaultAddons: NormalizedDefaultAddon[];
  selectedRegion: number | string;
  pricingReady: boolean;
  // Handlers
  onUpdateOtherItem: (id: number, field: string, value: unknown) => void;
  onDeleteOtherItem: (id: number) => void;
  // Price/availability
  isAddonRowAvailable: (item: OtherLineItem) => boolean;
  getAddonCostByIds: (addonId: number, optionId: number | null) => number;
  ensureCurrentOption: (options: SelectOption[], currentValue: number | string | null | undefined, makeLabel: (v: number | string) => string) => SelectOption[];
}

const OtherItemsTable: React.FC<OtherItemsTableProps> = ({
  otherLineItems, addons, filteredAddons, addonOptions, addonOptionPricing,
  defaultAddons, selectedRegion, pricingReady,
  onUpdateOtherItem, onDeleteOtherItem,
  isAddonRowAvailable, getAddonCostByIds, ensureCurrentOption,
}) => {
  if (otherLineItems.length === 0) return null;

  return (
    <Paper sx={{ mb: 3, elevation: 2, border: 1, borderColor: 'grey.300' }}>
      <Box sx={{ p: 1, backgroundColor: 'grey.300', borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>Other Items</Typography>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.NAME) }}>Component</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.OS2) }}>Option</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...SPACER_CELL_SX }}></TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.QTY), textAlign: 'center' }}>Qty</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.COST), textAlign: 'right' }}>Cost/mo</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.ACT) }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {otherLineItems.map((item) => {
              const addonId = Number(item.itemId) || null;
              const addonAvailable = isAddonRowAvailable(item);
              const price = addonId ? getAddonCostByIds(addonId, item.optionId ? Number(item.optionId) : null) : 0;
              const addonDisplayCost = (!pricingReady && item.savedLineCost != null) ? item.savedLineCost : (price * item.quantity);
              const addonOptionsList: SelectOption[] = filteredAddons
                .map((a) => ({ value: a.AddonId, label: a.AddonName }));
              const addonLabel = (val: number | string) => addons.find((a) => a.AddonId === Number(val))?.AddonName ?? String(val);
              const addonOptsWithCurrent = ensureCurrentOption(addonOptionsList, item.itemId, addonLabel);
              const includedEnvs = addonId
                ? defaultAddons.filter((da) => da.AddonId === addonId && (da.ApplyToVMs || da.ApplyToDatabaseVMs)).map((da) => da.EnvironmentCode).filter(Boolean)
                : [];
              const includedEnvsLabel = includedEnvs.length > 1
                ? includedEnvs.slice(0, -1).join(', ') + ' & ' + includedEnvs[includedEnvs.length - 1]
                : includedEnvs[0] ?? '';
              const allOpts = addonId ? (addonOptions[addonId] ?? []) : [];
              const availableOpts: SelectOption[] = allOpts
                .filter((opt) => addonOptionPricing[`${opt.AddonOptionId}-${selectedRegion}`]?.IsAvailable)
                .map((opt) => ({ value: opt.AddonOptionId, label: opt.OptionName }));
              const optLabel = (val: number | string) => (allOpts.find((o) => o.AddonOptionId === Number(val))?.OptionName ?? String(val));
              const optionOptsWithCurrent = ensureCurrentOption(availableOpts, item.optionId, optLabel);

              const selectedOption = item.optionId ? allOpts.find((o) => o.AddonOptionId === Number(item.optionId)) : null;
              const selectedAddon = addonId ? addons.find((a) => a.AddonId === addonId) : null;
              const description = selectedOption?.Description || selectedAddon?.Description || '';

              return (
                <TableRow key={item.id} hover sx={!addonAvailable ? rowErrorSX : undefined}>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.NAME) }}>
                    <TextField select size="small" value={item.itemId ?? ''} onChange={(e) => onUpdateOtherItem(item.id, 'itemId', e.target.value)} fullWidth>
                      {addonOptsWithCurrent.map((opt) => (
                        <MenuItem key={`ad-${opt.value}`} value={opt.value} disabled={opt.disabled}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>

                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.OS) }}>
                    {addonId && (allOpts.length > 0) ? (
                      <TextField select size="small" value={item.optionId ?? ''} onChange={(e) => onUpdateOtherItem(item.id, 'optionId', e.target.value)} fullWidth>
                        {optionOptsWithCurrent.map((opt) => <MenuItem key={`adopt-${opt.value}`} value={opt.value} disabled={opt.disabled}>{opt.label}</MenuItem>)}
                      </TextField>
                    ) : null}
                  </TableCell>

                  <TableCell sx={{ ...SPACER_CELL_SX, verticalAlign: 'middle', px: 1 }}>
                    {includedEnvs.length > 0 ? (
                      <Typography variant="body2" color="error" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                        * This addon is already included in the price for {includedEnvsLabel} VMs.
                      </Typography>
                    ) : description ? (
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'right' }}>
                        {description}
                      </Typography>
                    ) : null}
                  </TableCell>

                  <TableCell sx={{ ...CELL_W(COL.QTY), textAlign: 'center' }}>
                    <TextField type="number" size="small" value={item.quantity} onChange={(e) => onUpdateOtherItem(item.id, 'quantity', parseInt(e.target.value) || 1)} inputProps={{ min: 1, style: { textAlign: 'center' } }} fullWidth sx={NO_SPIN_SX} />
                  </TableCell>

                  <TableCell sx={{ ...CELL_W(COL.COST), textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {addonAvailable ? formatCurrency(addonDisplayCost) : <Typography color="error" fontWeight={700}>Unavailable</Typography>}
                  </TableCell>

                  <TableCell sx={{ ...CELL_W(COL.ACT), textAlign: 'right' }}>
                    <IconButton size="small" color="error" onClick={() => onDeleteOtherItem(item.id)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default OtherItemsTable;
